import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, imgUrl, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, X, Loader2 } from "lucide-react";

const FUELS = ["Benzin", "Diesel", "Elektro", "Hybrid", "LPG/Autogas"];
const TRANS = ["Schaltung", "Automatik"];
const BODY_TYPES = ["Limousine", "Kombi", "SUV/Geländewagen", "Coupé", "Cabrio", "Van/Kleinbus", "Kleinwagen", "Nutzfahrzeug"];
const CONDITIONS = ["Gebraucht", "Neu", "Tageszulassung", "Jahreswagen", "Vorführfahrzeug", "Oldtimer"];
const DRIVE_TYPES = ["Frontantrieb", "Heckantrieb", "Allrad"];
const EMISSION_CLASSES = ["Euro 6", "Euro 5", "Euro 4", "Euro 3", "Euro 2", "Euro 1", "Keine Angabe"];
const ENV_BADGES = ["Grün", "Gelb", "Rot", "Keine"];
const COLORS = ["Schwarz", "Weiß", "Grau", "Silber", "Blau", "Rot", "Grün", "Braun", "Beige", "Gelb", "Orange", "Andere"];
const YES_NO = ["Ja", "Nein"];
const CLIMATISATION = ["Keine Klimaanlage", "Klimaanlage", "Klimaautomatik", "2-Zonen-Klimaautomatik", "3-Zonen-Klimaautomatik", "4-Zonen-Klimaautomatik"];
const UPHOLSTERY = ["Stoff", "Teilleder", "Vollleder", "Alcantara", "Velours", "Andere"];
const INTERIOR_COLORS = ["Schwarz", "Grau", "Beige", "Braun", "Rot", "Andere"];
const HEADLIGHTS = ["Halogen", "Xenon", "LED", "Laserlicht", "Matrix-LED"];
const AVAILABILITY = ["Sofort verfügbar", "In 1 Woche", "In 2 Wochen", "In 1 Monat", "Nach Vereinbarung"];
const COMFORT_FEATURES = [
  "Einparkhilfe", "Einparkhilfe vorne", "Einparkhilfe hinten", "360° Kamera", "Rückfahrkamera",
  "Elektrische Fensterheber", "Elektrische Sitze", "Sitzheizung", "Sitzbelüftung", "Massagesitze",
  "Tempomat", "Abstandstempomat", "Keyless Entry", "Keyless Go", "Start/Stopp-Automatik",
  "Elektrische Heckklappe", "Panoramadach", "Schiebedach", "Standheizung", "Lenkradheizung",
  "Multifunktionslenkrad", "Regensensor", "Lichtsensor", "Beheizbare Frontscheibe",
];
const SAFETY_FEATURES = [
  "ABS", "ESP", "Traktionskontrolle", "Isofix", "Notbremsassistent", "Spurhalteassistent",
  "Spurwechselassistent", "Totwinkel-Assistent", "Verkehrszeichenerkennung", "Müdigkeitswarner",
  "Reifendruckkontrolle", "Berganfahrassistent", "Kurvenlicht", "Nebelscheinwerfer",
  "Alarmanlage", "Wegfahrsperre", "Airbags vorne", "Seitenairbags", "Kopfairbags",
];
const MULTIMEDIA_FEATURES = [
  "Navigationssystem", "Apple CarPlay", "Android Auto", "Bluetooth", "USB", "DAB-Radio",
  "Soundsystem", "Touchscreen", "Sprachsteuerung", "WLAN/Wifi Hotspot", "Induktionsladen",
  "Bordcomputer", "Head-up-Display", "Digitales Cockpit",
];
const EXTERIOR_FEATURES = [
  "Alufelgen", "Sommerreifen", "Winterreifen", "Ganzjahresreifen", "Dachreling",
  "Anhängerkupplung", "Abnehmbare Anhängerkupplung", "Elektrische Außenspiegel",
  "Beheizbare Außenspiegel", "Getönte Scheiben", "Sportpaket", "Sportfahrwerk",
  "Luftfederung", "Niveauregulierung",
];
const E_MOBILITY_FEATURES = [
  "Ladekabel Typ 2", "Schnellladefunktion", "Wärmepumpe", "Batteriezertifikat",
  "Batterie gekauft", "Batterie gemietet", "Wallbox-kompatibel",
];

const emptyDetails = {
  condition: "Gebraucht", body_type: "", first_registration: "", doors: "", seats: "",
  color: "", metallic: "", interior: "", interior_color: "", upholstery: "", climatisation: "",
  headlight_type: "", rim_size: "", drive_type: "", cubic_capacity: "",
  emission_class: "", environmental_badge: "", consumption: "", co2_emissions: "",
  hu_until: "", previous_owners: "", accident_vehicle: "Nein", damaged_vehicle: "Nein",
  service_history: "", non_smoker: "", vat_deductible: "", warranty: "",
  availability: "", financing_available: "", trade_in: "", leasing_takeover: "", export_available: "",
  comfort_features: [], safety_features: [], multimedia_features: [], exterior_features: [], e_mobility_features: [],
};

const empty = {
  title: "", brand: "", model: "", year: new Date().getFullYear(),
  price: "", mileage: "", fuel: "Benzin", transmission: "Schaltung",
  power_hp: "", description: "", location: "", details: emptyDetails, images: [],
};

export default function CarForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (id) {
      api.get(`/dealer/vehicles/${id}`).then(r => setForm({
        ...empty,
        ...r.data,
        details: { ...emptyDetails, ...(r.data.details || {}) },
      })).catch(() => toast.error("Inserat nicht gefunden"));
    }
  }, [id]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updDetail = (k, v) => setForm(f => ({ ...f, details: { ...(f.details || {}), [k]: v } }));
  const toggleDetailItem = (k, item) => setForm(f => {
    const current = f.details?.[k] || [];
    const next = current.includes(item) ? current.filter(x => x !== item) : [...current, item];
    return { ...f, details: { ...(f.details || {}), [k]: next } };
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const payload = {
      ...form,
      year: parseInt(form.year),
      price: parseFloat(form.price),
      mileage: parseInt(form.mileage),
      power_hp: parseInt(form.power_hp),
      details: cleanDetails(form.details),
    };
    try {
      if (id) {
        await api.patch(`/dealer/vehicles/${id}`, payload);
        toast.success("Aktualisiert");
      } else {
        await api.post("/dealer/vehicles", payload);
        toast.success("Inserat veröffentlicht");
      }
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm(f => ({ ...f, images: [...(f.images || []), data.path] }));
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Upload fehlgeschlagen");
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (p) => setForm(f => ({ ...f, images: f.images.filter(x => x !== p) }));

  const generateAI = async () => {
    if (!form.brand || !form.model || !form.year) {
      toast.error("Bitte zuerst Marke, Modell, Baujahr ausfüllen.");
      return;
    }
    setAiBusy(true);
    setForm(f => ({ ...f, description: "" }));
    try {
      const token = localStorage.getItem("am_token");
      const res = await fetch(`${API}/ai/describe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          brand: form.brand, model: form.model, year: parseInt(form.year) || 0,
          mileage: parseInt(form.mileage) || 0, fuel: form.fuel,
          transmission: form.transmission, power_hp: parseInt(form.power_hp) || 0,
          extra: "",
        }),
      });
      if (!res.ok) throw new Error("AI-Fehler");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const lines = ev.split("\n");
          let isData = false, isEvent = null, payload = "";
          for (const l of lines) {
            if (l.startsWith("event:")) isEvent = l.slice(6).trim();
            else if (l.startsWith("data:")) { isData = true; payload += l.slice(5).trim(); }
          }
          if (isEvent === "done") continue;
          if (isEvent === "error") { toast.error(payload); continue; }
          if (isData) {
            const text = payload.replace(/\\n/g, "\n");
            setForm(f => ({ ...f, description: (f.description || "") + text }));
          }
        }
      }
      toast.success("Beschreibung generiert");
    } catch (e) {
      toast.error("KI-Generierung fehlgeschlagen");
    } finally { setAiBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-8 max-w-4xl">
      <div className="swiss-card p-6">
        <h3 className="swiss-label text-[#16A34A] mb-4">Grunddaten</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Inserats-Titel *"><input required data-testid="vf-title" value={form.title} onChange={e=>upd("title", e.target.value)} className={inp} /></F>
          <F label="Marke *"><input required data-testid="vf-brand" value={form.brand} onChange={e=>upd("brand", e.target.value)} className={inp} /></F>
          <F label="Modell *"><input required data-testid="vf-model" value={form.model} onChange={e=>upd("model", e.target.value)} className={inp} /></F>
          <F label="Baujahr *"><input required type="number" min="1950" max="2030" data-testid="vf-year" value={form.year} onChange={e=>upd("year", e.target.value)} className={inp} /></F>
          <F label="Preis € *"><input required type="number" min="0" data-testid="vf-price" value={form.price} onChange={e=>upd("price", e.target.value)} className={inp} /></F>
          <F label="Kilometerstand *"><input required type="number" min="0" data-testid="vf-mileage" value={form.mileage} onChange={e=>upd("mileage", e.target.value)} className={inp} /></F>
          <F label="Kraftstoff *">
            <select data-testid="vf-fuel" value={form.fuel} onChange={e=>upd("fuel", e.target.value)} className={inp}>
              {FUELS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </F>
          <F label="Getriebe *">
            <select data-testid="vf-transmission" value={form.transmission} onChange={e=>upd("transmission", e.target.value)} className={inp}>
              {TRANS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>
          <F label="Leistung (PS) *"><input required type="number" min="0" data-testid="vf-power" value={form.power_hp} onChange={e=>upd("power_hp", e.target.value)} className={inp} /></F>
          <F label="Standort *"><input required data-testid="vf-location" value={form.location} onChange={e=>upd("location", e.target.value)} className={inp} /></F>
        </div>
      </div>

      <div className="swiss-card p-6">
        <h3 className="swiss-label text-[#16A34A] mb-4">Fahrzeugdetails</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Fahrzeugzustand">
            <Select value={form.details?.condition} onChange={v=>updDetail("condition", v)} options={CONDITIONS} />
          </F>
          <F label="Karosserieform">
            <Select value={form.details?.body_type} onChange={v=>updDetail("body_type", v)} options={BODY_TYPES} placeholder="Bitte wählen" />
          </F>
          <F label="Erstzulassung"><input type="month" value={form.details?.first_registration || ""} onChange={e=>updDetail("first_registration", e.target.value)} className={inp} /></F>
          <F label="HU bis"><input type="month" value={form.details?.hu_until || ""} onChange={e=>updDetail("hu_until", e.target.value)} className={inp} /></F>
          <F label="Türen"><input type="number" min="1" max="6" value={form.details?.doors || ""} onChange={e=>updDetail("doors", e.target.value)} className={inp} /></F>
          <F label="Sitzplätze"><input type="number" min="1" max="9" value={form.details?.seats || ""} onChange={e=>updDetail("seats", e.target.value)} className={inp} /></F>
          <F label="Außenfarbe">
            <Select value={form.details?.color} onChange={v=>updDetail("color", v)} options={COLORS} placeholder="Bitte wählen" />
          </F>
          <F label="Metallic">
            <Select value={form.details?.metallic} onChange={v=>updDetail("metallic", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Polsterung">
            <Select value={form.details?.upholstery} onChange={v=>updDetail("upholstery", v)} options={UPHOLSTERY} placeholder="Bitte wählen" />
          </F>
          <F label="Innenraumfarbe">
            <Select value={form.details?.interior_color} onChange={v=>updDetail("interior_color", v)} options={INTERIOR_COLORS} placeholder="Bitte wählen" />
          </F>
          <F label="Innenausstattung"><input value={form.details?.interior || ""} onChange={e=>updDetail("interior", e.target.value)} className={inp} placeholder="z. B. Sportsitze, Leder beige" /></F>
          <F label="Klimatisierung">
            <Select value={form.details?.climatisation} onChange={v=>updDetail("climatisation", v)} options={CLIMATISATION} placeholder="Bitte wählen" />
          </F>
          <F label="Scheinwerfer">
            <Select value={form.details?.headlight_type} onChange={v=>updDetail("headlight_type", v)} options={HEADLIGHTS} placeholder="Bitte wählen" />
          </F>
          <F label="Felgengröße"><input value={form.details?.rim_size || ""} onChange={e=>updDetail("rim_size", e.target.value)} className={inp} placeholder="z. B. 18 Zoll" /></F>
          <F label="Antriebsart">
            <Select value={form.details?.drive_type} onChange={v=>updDetail("drive_type", v)} options={DRIVE_TYPES} placeholder="Bitte wählen" />
          </F>
          <F label="Hubraum (cm³)"><input type="number" min="0" value={form.details?.cubic_capacity || ""} onChange={e=>updDetail("cubic_capacity", e.target.value)} className={inp} /></F>
          <F label="Anzahl Vorbesitzer"><input type="number" min="0" value={form.details?.previous_owners || ""} onChange={e=>updDetail("previous_owners", e.target.value)} className={inp} /></F>
        </div>
      </div>

      <div className="swiss-card p-6">
        <h3 className="swiss-label text-[#16A34A] mb-4">Umwelt, Zustand & Verkauf</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Schadstoffklasse">
            <Select value={form.details?.emission_class} onChange={v=>updDetail("emission_class", v)} options={EMISSION_CLASSES} placeholder="Bitte wählen" />
          </F>
          <F label="Umweltplakette">
            <Select value={form.details?.environmental_badge} onChange={v=>updDetail("environmental_badge", v)} options={ENV_BADGES} placeholder="Bitte wählen" />
          </F>
          <F label="Verbrauch kombiniert (l/100 km oder kWh/100 km)"><input value={form.details?.consumption || ""} onChange={e=>updDetail("consumption", e.target.value)} className={inp} placeholder="z. B. 5,8" /></F>
          <F label="CO₂-Emissionen (g/km)"><input type="number" min="0" value={form.details?.co2_emissions || ""} onChange={e=>updDetail("co2_emissions", e.target.value)} className={inp} /></F>
          <F label="Unfallfahrzeug">
            <Select value={form.details?.accident_vehicle} onChange={v=>updDetail("accident_vehicle", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Beschädigtes Fahrzeug">
            <Select value={form.details?.damaged_vehicle} onChange={v=>updDetail("damaged_vehicle", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Scheckheftgepflegt">
            <Select value={form.details?.service_history} onChange={v=>updDetail("service_history", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Nichtraucherfahrzeug">
            <Select value={form.details?.non_smoker} onChange={v=>updDetail("non_smoker", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="MwSt. ausweisbar">
            <Select value={form.details?.vat_deductible} onChange={v=>updDetail("vat_deductible", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Garantie">
            <Select value={form.details?.warranty} onChange={v=>updDetail("warranty", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Verfügbarkeit">
            <Select value={form.details?.availability} onChange={v=>updDetail("availability", v)} options={AVAILABILITY} placeholder="Bitte wählen" />
          </F>
          <F label="Finanzierung möglich">
            <Select value={form.details?.financing_available} onChange={v=>updDetail("financing_available", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Inzahlungnahme möglich">
            <Select value={form.details?.trade_in} onChange={v=>updDetail("trade_in", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Leasingübernahme">
            <Select value={form.details?.leasing_takeover} onChange={v=>updDetail("leasing_takeover", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
          <F label="Export möglich">
            <Select value={form.details?.export_available} onChange={v=>updDetail("export_available", v)} options={YES_NO} placeholder="Keine Angabe" />
          </F>
        </div>
      </div>

      <div className="swiss-card p-6">
        <h3 className="swiss-label text-[#16A34A] mb-4">Ausstattung & Extras</h3>
        <div className="space-y-6">
          <CheckboxGroup title="Komfort" field="comfort_features" options={COMFORT_FEATURES} selected={form.details?.comfort_features} onToggle={toggleDetailItem} />
          <CheckboxGroup title="Sicherheit & Assistenz" field="safety_features" options={SAFETY_FEATURES} selected={form.details?.safety_features} onToggle={toggleDetailItem} />
          <CheckboxGroup title="Multimedia" field="multimedia_features" options={MULTIMEDIA_FEATURES} selected={form.details?.multimedia_features} onToggle={toggleDetailItem} />
          <CheckboxGroup title="Exterieur & Fahrwerk" field="exterior_features" options={EXTERIOR_FEATURES} selected={form.details?.exterior_features} onToggle={toggleDetailItem} />
          {(form.fuel === "Elektro" || form.fuel === "Hybrid") && (
            <CheckboxGroup title="E-Mobilität" field="e_mobility_features" options={E_MOBILITY_FEATURES} selected={form.details?.e_mobility_features} onToggle={toggleDetailItem} />
          )}
        </div>
      </div>

      <div className="swiss-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="swiss-label text-[#16A34A]">Bilder</h3>
          <button type="button" data-testid="vf-upload-btn" onClick={() => fileRef.current?.click()} className="swiss-btn-secondary text-sm py-2 px-4">
            <Upload className="w-4 h-4" /> Bilder hochladen
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="vf-file-input" />
        </div>
        {form.images?.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8 border border-dashed border-gray-300">Noch keine Bilder. Empfohlen: 4-8 hochwertige Aufnahmen.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {form.images.map((p, i) => (
              <div key={p+i} className="relative aspect-[4/3] bg-gray-100 group">
                <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(p)} data-testid={`vf-remove-img-${i}`} className="absolute top-1 right-1 bg-white text-[#16A34A] p-1 opacity-0 group-hover:opacity-100"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="swiss-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="swiss-label text-[#16A34A]">Beschreibung</h3>
          <button type="button" disabled={aiBusy} data-testid="vf-ai-btn" onClick={generateAI} className="swiss-btn-primary text-sm py-2 px-4">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {aiBusy ? "Generiere…" : "KI-Beschreibung generieren"}
          </button>
        </div>
        <textarea required data-testid="vf-description" value={form.description} onChange={e=>upd("description", e.target.value)} rows={10} className="w-full border border-gray-200 p-4 outline-none focus:border-[#16A34A] text-sm leading-relaxed" placeholder="Beschreibung deines Fahrzeugs…" />
      </div>

      {error && <div data-testid="vf-error" className="text-sm text-red-600 bg-red-50 border border-red-200 p-3">{error}</div>}

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={() => nav("/dashboard")} className="swiss-btn-secondary">Abbrechen</button>
        <button type="submit" disabled={loading} data-testid="vf-submit" className="swiss-btn-primary">
          {loading ? "Speichern…" : (id ? "Speichern" : "Inserat veröffentlichen")}
        </button>
      </div>
    </form>
  );
}

const inp = "w-full mt-2 border border-gray-200 py-2.5 px-3 outline-none focus:border-[#16A34A] text-sm";
function F({ label, children }) {
  return <div><label className="swiss-label">{label}</label>{children}</div>;
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <select value={value || ""} onChange={e=>onChange(e.target.value)} className={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o || "empty"} value={o}>{o || "Keine Angabe"}</option>)}
    </select>
  );
}

function cleanDetails(details = {}) {
  return Object.fromEntries(
    Object.entries(details).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value !== null && value !== undefined;
    })
  );
}

function CheckboxGroup({ title, field, options, selected = [], onToggle }) {
  return (
    <fieldset>
      <legend className="swiss-label text-gray-500 mb-3">{title}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {options.map(option => (
          <label key={option} className="flex items-center gap-2 border border-gray-200 px-3 py-2 text-sm cursor-pointer hover:border-[#16A34A]">
            <input
              type="checkbox"
              checked={(selected || []).includes(option)}
              onChange={() => onToggle(field, option)}
              className="accent-[#16A34A]"
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
